import { useAccountState } from '../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef, useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Link,
    SelectTabData,
    SelectTabEvent,
    Spinner,
    Tab,
    TabList,
    TabValue,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
    Text,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { ArrowDownload20Regular, DocumentPdfRegular, HistoryRegular, TableRegular } from '@fluentui/react-icons';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';
import { Column, Row } from '../common';
import { Paths } from '../common/routes';
import { AuditItem } from '../../data/types';
import { useAuditHistoryService } from '../../data/services/useAuditHistoryService';
import { useDialogControls } from './useDialogControls';
import { useCaseService, useInvoiceService, usePartyService } from '../../data/services';
import { useMediaSizes } from '../common/Styles/useMediaSizes';
import { AuditHistoryDoc } from './AuditHistoryDoc';
import { CaseTabs } from '../pages/Tabs/CaseTabs';
import { SettingsPageTabs } from '../pages';
import { appTokens } from '../common/Styles/appTokens';

const useStyles = makeStyles({
    surface: {
        maxWidth: '90vw',
        width: 'min(1400px, 95vw)',
    },
    body: {
        maxWidth: '100%',
        width: '100%',
    },
    tableWrap: {
        maxHeight: '60vh',
        width: '100%',
        overflowY: 'auto',
        overflowX: 'auto',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    empty: {
        paddingBlock: '2rem',
    },
    viewToggle: {
        marginBottom: '0.5rem',
    },
    pdfWrap: {
        height: '60vh',
        width: '100%',
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        overflow: 'hidden',
    },
    pdfViewer: {
        height: '100%',
        width: '100%',
        border: 'none',
    },
    downloadLink: {
        textDecoration: 'none',
    },
    headerRow: {
        backgroundColor: appTokens.table.header.background,
        borderBottomColor: appTokens.table.header.border,
    },
    row: {
        backgroundColor: tokens.colorNeutralBackground1,
        ':nth-child(even)': {
            backgroundColor: tokens.colorNeutralBackground2,
        },
    },
    timestampCell: {
        whiteSpace: 'nowrap',
    },
    stackedCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.125rem',
        paddingBlock: '0.5rem',
    },
    stackedLabel: {
        fontWeight: tokens.fontWeightSemibold,
        marginInlineEnd: '0.25rem',
    },
});

export type AuditHistoryDialogProps = Omit<Partial<DialogProps>, 'onOpenChange'> & {
    children?: JSX.Element;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    // When caseId is provided, loads case audit history; otherwise loads account-level history.
    caseId?: string;
    title?: string;
};

const formatTimestamp = (ts?: Date) => {
    if (!ts) return '';
    const d = ts instanceof Date ? ts : new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString();
};

export const AuditHistoryDialog = forwardRef((props: AuditHistoryDialogProps, _ref) => {
    const { children, modalType, open, onOpenChange, caseId, title } = props;

    //Styles
    const styles = useStyles();

    //Hooks
    const { primaryAccount, getUserName } = useAccountState();
    const caseService = useCaseService();
    const mediaSizes = useMediaSizes();
    const partyService = usePartyService();
    const invoiceService = useInvoiceService();
    const navTo = useNavigate();

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler, closeDialog] = useDialogControls(open, onOpenChange);

    //Services
    const auditService = useAuditHistoryService(caseId);

    //State
    const [items, setItems] = useState<AuditItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>();
    const [viewMode, setViewMode] = useState<TabValue>('table');

    //Effects
    useEffect(() => {
        if (!dialogOpen) return;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(undefined);
            try {
                const result = caseId
                    ? await auditService.getCaseAuditHistoryAsync('', caseId)
                    : await auditService.getAccountAuditHistoryAsync('');
                if (cancelled) return;
                setItems(result ?? []);
            } catch {
                if (cancelled) return;
                setError('Failed to load audit history.');
                setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();

        return () => {
            cancelled = true;
        };
    }, [dialogOpen, caseId]);

    const onClickClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        closeDialog();
    };

    const resolvedTitle = title ?? (caseId ? 'Case Audit History' : 'Account Audit History');
    const subtitle = caseId ? caseService.findCaseById(caseId)?.caseName : primaryAccount?.account.accountName;
    const pdfFileName = `${resolvedTitle.replace(/[^a-zA-Z0-9]/g, '_')}${subtitle ? `_${subtitle.replace(/[^a-zA-Z0-9]/g, '_')}` : ''}.pdf`;

    const onTabSelect = (_e: SelectTabEvent, data: SelectTabData) => {
        setViewMode(data.value);
    };

    const onPartyLinkClick = (partyId: string) => {
        closeDialog();
        navTo(Paths.clientsParties, { state: { partyId } });
    };

    const onCaseLinkClick = (linkCaseId: string) => {
        closeDialog();
        navTo(Paths.viewEditCase(linkCaseId));
    };

    const onInvoiceLinkClick = (caseId: string, invoiceId: string) => {
        closeDialog();
        navTo(Paths.viewEditCase(caseId), { state: { tab: CaseTabs.invoices, invoiceId } });
    };

    const onCaseDataLinkClick = () => {
        closeDialog();
        navTo(Paths.accountSettings, { state: { tab: SettingsPageTabs.caseData } });
    };

    //Replaces [partyId:<id>] and [caseId:<id>] tokens in a description with links to the party/case
    const parseDescription = (caseId?: string, descrip?: string) => {
        if (!descrip) return descrip;

        const tokenPattern = /\[(partyId|caseId|invoiceId|caseType|expenseType|caseStatus):([^\]]+)\]/g;
        const parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match;

        while ((match = tokenPattern.exec(descrip)) !== null) {
            if (match.index > lastIndex) {
                parts.push(descrip.substring(lastIndex, match.index));
            }

            const tokenType = match[1];
            const tokenId = match[2];

            if (tokenType === 'partyId') {
                const party = partyService.findParty(tokenId);
                if (party) {
                    parts.push(
                        <Link key={`party-${match.index}`} onClick={() => onPartyLinkClick(tokenId)}>
                            {party.name}
                        </Link>,
                    );
                } else {
                    parts.push(match[0]);
                }
            } else if (tokenType === 'caseId') {
                const linkedCase = caseService.findCaseById(tokenId);
                if (linkedCase) {
                    parts.push(
                        <Link key={`case-${match.index}`} onClick={() => onCaseLinkClick(tokenId)}>
                            {linkedCase.caseName}
                        </Link>,
                    );
                } else {
                    parts.push(match[0]);
                }
            } else if (tokenType === 'invoiceId') {
                const linkedInvoice = invoiceService.invoices.find((invoice) => invoice.invoiceId === tokenId);
                if (caseId && linkedInvoice) {
                    parts.push(
                        <Link key={`invoice-${match.index}`} onClick={() => onInvoiceLinkClick(caseId, tokenId)}>
                            Invoice {linkedInvoice.invoiceNumber}
                        </Link>,
                    );
                } else {
                    parts.push(match[0]);
                }
            } else if (tokenType === 'caseType' || tokenType === 'caseStatus' || tokenType === 'expenseType') {
                parts.push(
                    <Link key={`caseType-${match.index}`} onClick={() => onCaseDataLinkClick()}>
                        {tokenId}
                    </Link>,
                );
            } 

            lastIndex = match.index + match[0].length;
        }

        if (lastIndex < descrip.length) {
            parts.push(descrip.substring(lastIndex));
        }

        return parts.length > 0 ? parts : descrip;
    };

    return (
        <Dialog modalType={modalType} open={dialogOpen} onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>{children as any}</DialogTrigger>
            <DialogSurface className={styles.surface}>
                <DialogBody className={styles.body}>
                    <DialogTitle>
                        <Row gap='medium' noWrap={false}>
                            <Column width='auto' vAlign='middle'>
                                <HistoryRegular />
                            </Column>
                            <Column width='auto'>{resolvedTitle}</Column>
                            <Column>
                                <Text weight='semibold' size={500} wrap={false}>
                                    {caseId
                                        ? ` - ${caseService.findCaseById(caseId)?.caseName}`
                                        : ` - ${primaryAccount?.account.accountName}`}
                                </Text>
                            </Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        {!loading && !error && (
                            <Row gap='medium' className={styles.viewToggle}>
                                <Column width='auto'>
                                    <TabList selectedValue={viewMode} onTabSelect={onTabSelect} size='small'>
                                        <Tab value='table' icon={<TableRegular />}>
                                            Table
                                        </Tab>
                                        <Tab value='pdf' icon={<DocumentPdfRegular />}>
                                            PDF
                                        </Tab>
                                    </TabList>
                                </Column>
                                {viewMode === 'pdf' && items.length > 0 && (
                                    <Column width='auto'>
                                        <PDFDownloadLink
                                            className={styles.downloadLink}
                                            document={
                                                <AuditHistoryDoc
                                                    title={resolvedTitle}
                                                    subtitle={subtitle}
                                                    items={items}
                                                    getUserName={getUserName}
                                                />
                                            }
                                            fileName={pdfFileName}
                                        >
                                            {({ loading: dlLoading }) => (
                                                <Button
                                                    appearance='secondary'
                                                    icon={<ArrowDownload20Regular />}
                                                    disabled={dlLoading}
                                                    data-testid='audit-history-dialog-download-pdf-button'
                                                >
                                                    {dlLoading ? 'Generating...' : 'Download PDF'}
                                                </Button>
                                            )}
                                        </PDFDownloadLink>
                                    </Column>
                                )}
                            </Row>
                        )}
                        {loading && (
                            <Column vAlign='middle' align='center' className={styles.empty}>
                                <Spinner label='Loading audit history...' />
                            </Column>
                        )}
                        {!loading && error && (
                            <Column vAlign='middle' align='center' className={styles.empty}>
                                <Text>{error}</Text>
                            </Column>
                        )}
                        {!loading && !error && viewMode === 'pdf' && (
                            <Column className={styles.pdfWrap}>
                                <PDFViewer className={styles.pdfViewer} showToolbar>
                                    <AuditHistoryDoc
                                        title={resolvedTitle}
                                        subtitle={subtitle}
                                        items={items}
                                        getUserName={getUserName}
                                    />
                                </PDFViewer>
                            </Column>
                        )}
                        {!loading && !error && viewMode === 'table' && items.length === 0 && (
                            <Column vAlign='middle' align='center' className={styles.empty}>
                                <Text>No audit history available.</Text>
                            </Column>
                        )}
                        {!loading && !error && viewMode === 'table' && items.length > 0 && (
                            <Column className={styles.tableWrap}>
                                <Table size='small' aria-label='Audit history'>
                                    {!mediaSizes.isMobile && (
                                        <TableHeader>
                                            <TableRow className={styles.headerRow}>
                                                <TableHeaderCell>When</TableHeaderCell>
                                                <TableHeaderCell>Actor</TableHeaderCell>
                                                <TableHeaderCell>Action</TableHeaderCell>
                                                <TableHeaderCell>Object</TableHeaderCell>
                                                <TableHeaderCell>Detail</TableHeaderCell>
                                            </TableRow>
                                        </TableHeader>
                                    )}
                                    <TableBody>
                                        {items.map((item, idx) => {
                                            const key = `${item.itemId ?? 'audit'}-${idx}`;
                                            const actorText = `${getUserName(item.actor?.userId ?? '')}${item.actor?.role ? ` - ${item.actor.role}` : ''}`;

                                            if (mediaSizes.isMobile) {
                                                return (
                                                    <TableRow key={key} className={styles.row}>
                                                        <TableCell>
                                                            <div className={styles.stackedCell}>
                                                                <Text>
                                                                    <Text className={styles.stackedLabel}>When:</Text>
                                                                    {formatTimestamp(item.timestamp)}
                                                                </Text>
                                                                <Text>
                                                                    <Text className={styles.stackedLabel}>Actor:</Text>
                                                                    {actorText}
                                                                </Text>
                                                                <Text>
                                                                    <Text className={styles.stackedLabel}>Action:</Text>
                                                                    {item.action}
                                                                </Text>
                                                                <Text>
                                                                    <Text className={styles.stackedLabel}>Object:</Text>
                                                                    {item.detail?.objectType}
                                                                </Text>
                                                                <Text>
                                                                    <Text className={styles.stackedLabel}>Detail:</Text>
                                                                    {parseDescription(item.caseId, item.detail?.description)}
                                                                </Text>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }

                                            return (
                                                <TableRow key={key} className={styles.row}>
                                                    <TableCell className={styles.timestampCell}>
                                                        <Column padding='py-2'>
                                                            <Text>{formatTimestamp(item.timestamp)}</Text>
                                                        </Column>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Column padding='py-2'>
                                                            <Text>{actorText}</Text>
                                                        </Column>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Column padding='py-2'>
                                                            <Text>{item.action}</Text>
                                                        </Column>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Column padding='py-2'>
                                                            <Text>{item.detail?.objectType}</Text>
                                                        </Column>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Column padding='py-2'>
                                                            <Text>{parseDescription(item.caseId, item.detail?.description)}</Text>
                                                        </Column>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </Column>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance='secondary'
                                onClick={onClickClose}
                                data-testid='audit-history-dialog-close-button'
                            >
                                Close
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
});
